import { useAuth } from "@/hooks/useAuth";
import { useAdviserIdentity } from "@/hooks/useAdviserIdentity";

// Phase 3 (F5): a UX-only gate for the "published insight" / public-marketing surfaces that
// external advisers are entitled to.
//
// AUDIENCE-SEPARATION NOTE (Phase 4 build-wall): this hook lives in its OWN neutral module,
// deliberately split out of `useSurfaceAccess.ts`. The adviser + public build entries import
// `usePublishedInsightAccess` (AdviserInsights does) but MUST NOT pull the research-only
// `useSurfaceAccess`/`useResearchHubAccess` hooks into their import graph — those name the
// proprietary `has_surface_access` RPC. Keeping this neutral gate in a separate file means the
// research RPC string is physically absent from the adviser/public bundles. `useSurfaceAccess.ts`
// re-exports this symbol so every existing internal import site is unchanged.
//
// This is DELIBERATELY separate from the research surface gate: it answers only "may this
// principal see the published-insight / adviser-portal marketing surfaces?" — true for staff
// (any staffMember) OR a confirmed external adviser. It governs client UX only; the real wall is
// the Phase 2 tier RLS (S3/S5), so a bug here cannot leak proprietary rows.

export interface PublishedInsightAccess {
  loading: boolean;
  canReadPublishedInsight: boolean;
}

export function usePublishedInsightAccess(): PublishedInsightAccess {
  const { staffMember } = useAuth();
  const adviser = useAdviserIdentity();
  return {
    loading: adviser.loading,
    canReadPublishedInsight: !!staffMember || adviser.isAdviser,
  };
}
